import React, { useState, useEffect } from "react";
import { Favorite, ChatBubbleOutline } from "@mui/icons-material";
import { BlogPost } from "../types/blog";
import CommentSection from "./CommentSection";
import CommentBox from "./CommentBox";
import CommentCount from "./CommentCount";
import LoadingSpinner from "./LoadingSpinner";

interface ArticlePageProps {
  post: BlogPost;
  onLike: () => void;
  onAddComment: (content: string) => Promise<void>;
  onDeleteComment: (id: string) => Promise<void>;
}

export default function ArticlePage({
  post,
  onLike,
  onAddComment,
  onDeleteComment,
}: ArticlePageProps) {
  const [comments, setComments] = useState(post.comments || []);
  const [useCommentBox, setUseCommentBox] = useState(true);
  const [commentCount, setCommentCount] = useState(0);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  // Update local comments state when post.comments changes
  useEffect(() => {
    setComments(post.comments || []);
  }, [post.comments]);

  // Handle comment deletion locally after the backend operation is done
  const handleCommentDeleted = (id: string) => {
    setComments(comments.filter((comment) => comment.id !== id));
  };

  const handleDeleteComment = async (id: string) => {
    try {
      // Call the parent's onDeleteComment function
      await onDeleteComment(id);
      // Update local state
      handleCommentDeleted(id);
    } catch (err) {
      console.error("Error deleting comment:", err);
      setCommentError("Failed to delete comment");
    }
  };

  const handleAddCommentWithErrorHandling = async (content: string) => {
    try {
      setIsSubmittingComment(true);
      setCommentError(null);
      await onAddComment(content);
    } catch (err) {
      console.error("Error adding comment:", err);
      setCommentError("Failed to add comment");
    } finally {
      setIsSubmittingComment(false);
    }
  };

  // Handle comment count change from commentbox.io
  const handleCommentCountChange = (count: number) => {
    setCommentCount(count);
  };

  return (
    <main className="mx-auto max-w-5xl px-4 py-8 animate-fade-in sm:px-6 sm:py-12 lg:px-8">
      <article className="overflow-hidden rounded-2xl border border-neutral-200/80 bg-white/84 shadow-[0_24px_80px_rgba(23,23,23,0.07)] backdrop-blur dark:border-neutral-800 dark:bg-neutral-900/70 dark:shadow-none">
        {post.imageUrl && (
          <div className="aspect-[16/9] w-full overflow-hidden bg-neutral-200 dark:bg-neutral-900">
            <img
              src={post.imageUrl}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="px-6 py-8 sm:px-8 md:px-12 md:py-12">
          <h1 className="text-balance text-4xl font-semibold leading-[1.05] tracking-tight text-neutral-950 dark:text-neutral-50 sm:text-5xl md:text-6xl">
            {post.title}
          </h1>
          <div className="mb-10 mt-6 flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-neutral-200/80 pb-6 text-sm text-neutral-600 dark:border-neutral-800 dark:text-neutral-400 sm:text-base">
            <span className="font-semibold text-neutral-800 dark:text-neutral-200">{post.author}</span>
            <span>
              {new Date(post.date).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </span>
            <div className="flex w-full items-center gap-4 sm:ml-auto sm:w-auto">
              <CommentCount count={commentCount} showZero={true} />
              <button
                onClick={onLike}
                className="flex items-center gap-1.5 rounded-full px-3 py-2 text-red-500 transition-smooth hover:bg-red-50 hover:text-red-600 active:scale-[0.98] dark:text-red-400 dark:hover:bg-red-950/30 dark:hover:text-red-300"
                aria-label="Like this post"
              >
                <Favorite sx={{ width: 20, height: 20 }} />
                <span className="font-medium">{post.likes || 0}</span>
              </button>
            </div>
          </div>
          <div
            className="prose prose-neutral max-w-none prose-base prose-p:leading-8 prose-p:text-neutral-700 prose-headings:font-semibold prose-headings:tracking-tight prose-headings:text-neutral-950 prose-a:text-primary-700 prose-a:no-underline hover:prose-a:underline prose-strong:text-neutral-950 prose-code:rounded prose-code:bg-neutral-100 prose-code:px-1 prose-code:py-0.5 prose-code:text-primary-700 prose-pre:bg-neutral-950 prose-img:rounded-2xl dark:prose-invert dark:prose-p:text-neutral-300 dark:prose-headings:text-neutral-50 dark:prose-a:text-primary-300 dark:prose-strong:text-neutral-50 dark:prose-code:bg-neutral-800 dark:prose-code:text-primary-300 dark:prose-pre:bg-neutral-950 sm:prose-lg"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </div>

        {/* Comments Section */}
        <div className="border-t border-neutral-200/80 bg-neutral-50/70 p-6 dark:border-neutral-800 dark:bg-neutral-950/28 sm:p-8 md:p-12">
          {useCommentBox ? (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="rounded-2xl bg-primary-600 p-2 text-white dark:bg-primary-500">
                  <ChatBubbleOutline
                    sx={{ width: 20, height: 20 }}
                    className="text-white"
                  />
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">Comments</h3>
              </div>
              <CommentBox
                pageId={post.id}
                onCommentCountChange={handleCommentCountChange}
              />
            </div>
          ) : (
            <div>
              {commentError && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400 rounded-lg flex items-center justify-between animate-slide-up">
                  <span>{commentError}</span>
                  <button
                    onClick={() => setCommentError(null)}
                    className="ml-2 text-red-800 dark:text-red-300 hover:text-red-900 dark:hover:text-red-200 text-xl leading-none"
                  >
                    ×
                  </button>
                </div>
              )}
              <CommentSection
                comments={comments}
                onAddComment={handleAddCommentWithErrorHandling}
                onDeleteComment={handleDeleteComment}
              />
              {isSubmittingComment && (
                <div className="mt-2">
                  <LoadingSpinner size="sm" message="Adding comment..." />
                </div>
              )}
              <div className="mt-4 text-sm text-neutral-600 dark:text-neutral-400">
                <button
                  onClick={() => setUseCommentBox(true)}
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline transition-colors"
                >
                  Switch to commentbox.io
                </button>
              </div>
            </div>
          )}
        </div>
      </article>
    </main>
  );
}
